import { Navigate, useLocation } from "react-router-dom";
import { AppRoutes } from "../../constants/routes";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useEffect } from "react";
import SectionLoader from "../_include/SectionLoader";
import PartnersController, { fetchPartnerDoc } from "../../actions/partner_controller";
import { useHasPartner } from "../../hooks/user";
import { selectAllPartners } from "../../store/reducers/partner/list";
import { LocalStorageKeys } from "../../constants/local_storage";
import { useLocalStorage } from "../../hooks/local_storage";
import { usePartnerID } from "../../hooks/partners";

/**
 * A private route that checks if the user is authenticated and has a partner.
 * If not, it redirects to the login page.
 * If the partner is not loaded, it shows a loading spinner.
 */
const PrivateRoute = ({ children }: { children: JSX.Element }) => {
    const user = useAppSelector(state => state.user);
    const location = useLocation();

    const hasPartners = useHasPartner();

    const partnerID = usePartnerID();
    const partnerLoaded = useAppSelector(state => Boolean(state.partners.selected.partner));

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (partnerID && !partnerLoaded) {
            dispatch(fetchPartnerDoc(partnerID));
        }
    }, [dispatch, partnerID, partnerLoaded]);

    const partners = useAppSelector(selectAllPartners);

    const [localStoragePartnerID] = useLocalStorage(LocalStorageKeys.PARTNER_ID);

    /** Load partners list. */
    useEffect(() => {
        if (hasPartners) { // load partners list
            dispatch(PartnersController.listPartners());
        }
    }, [hasPartners]);

    /** Set partner selected by default. */
    useEffect(() => {
        if (partners.length > 0) {
            let defaultSelected = partners[0]; // select first partner in list by default
            if (localStoragePartnerID) { // select partner from URL param partnerID
                let paramPartner = partners.find(p => p.ID === localStoragePartnerID);
                if (paramPartner) defaultSelected = paramPartner;
            }

            dispatch(PartnersController.select(defaultSelected));
        }
    }, [partners, localStoragePartnerID]);

    if (!user.isAuthenticated) return <Navigate to={AppRoutes.LOGIN} state={{ from: location }} />;

    // TODO: allow user to logout if has no partner
    if (!partnerLoaded) return <SectionLoader />;

    return children;
};

export default PrivateRoute;
